import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import AnswerDndWrapper from './AnswerDndWrapper'
import Answer from 'components/answers/Answer'
import ThinAnswer from 'components/answers/ThinAnswer'
import cn from 'classnames'
import { isMobile } from 'lib/device'
import { uniqBy } from 'lodash'
import api from 'api/api'
import { flushSync } from 'react-dom'
import { getCachedAnswer, isHydratedAnswerCacheEntry } from 'state/cache'
import { prepareOpenRight } from 'refactor/panels/prepareOpenRight'
import { revealThreadOpen } from 'lib/threadOpenReveal'
import AnswerInView from 'components/answers/AnswerInView'
import VirtualAnswerInView from 'components/answers/VirtualAnswerInView'
import ThreadNaturalSegments, {
    THREAD_NATURAL_FLOW_SEGMENT_SIZE,
} from 'components/answers/ThreadNaturalSegments'
import ThreadPanelActivityBoundary from 'components/answers/ThreadPanelActivityBoundary'
import dndWrapperStyles from './answer-dnd-wrapper.module.scss'
import { useAnswersDndRegistry } from 'components/answers/dndKit/GlobalAnswersDndProvider'
import perms from 'lib/perms'
import useUserHasFeature, { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { getAnswerRenderSurface } from 'lib/answerRenderSurface'
import { resolveEstimatedAnswerRowHeight } from 'lib/answerGeometryFrame'
import {
    answerHeightContentSignature,
    resolveHeightEstimationSource,
} from 'lib/answerHeightFingerprint'
import { mediaBucketsEnabled } from 'lib/mediaBuckets'
import { qaScrollSeedDataAttributes } from 'lib/qaScrollSeedMetadata'
import { ScrollResizeCompensationProvider } from 'lib/scrollResizeCompensationContext'
import { RowGeometryEventsProvider } from 'lib/rowGeometryEvents'
import { updateStaticRowQaProbe } from 'lib/staticGeometryQaProbe'
import { readStaticAnswerRowMeasurement } from 'lib/staticGeometryRowMeasurements'
import { useStaticGeometryRowMeasurements } from 'lib/useStaticGeometryRowMeasurements'
import { usePanelVirtualizer } from 'lib/usePanelVirtualizer'
import {
    findAnswerTargetElement,
    isAnswerElementNearTarget,
    scrollAnswerElementIntoView,
} from 'lib/answerTargetScroll'
import {
    findAnswerRowElement,
    placeAnswerRowAtTarget,
    OPEN_PLACEMENT_MAX_START_SCROLL_TOP_PX,
} from 'lib/threadOpenPlacement'
import { markPanelNavMetric } from 'lib/panelNavMetrics'
import { queueAnswerHydration } from 'state/answerHydrationQueue'
import {
    resolveAnswerDeepLinkTarget,
    shouldVirtualizeAnswerTail,
    virtualAnswerTargetWindowIds,
    VIRTUAL_TAIL_ANSWER_MIN_COUNT,
} from 'components/answers/answerTailVirtualization'
import {
    resolveDirectMessageLatestAnswer,
    resolveQuestStreamRenderedAnswers,
} from 'lib/questStreamRenderedAnswers'
import { dmChatDayLabel, dmChatRunFor, isDmChatQuest } from 'lib/dmChat'
import { isPostLensStreamQuest, resolveQuestStreamOthersRow } from 'lib/streamCards'
import QuestStreamOthersRow from 'components/quests/Quest/QuestStreamOthersRow'
import { shouldRemeasureRowWidth, ANSWER_EDITING_RESERVE_EXTRA_PX } from 'lib/questStreamHeight'
import { sameArrayItems, sameSetMembers } from 'lib/sameStateItems'
import { shouldCollapseAnswerText } from './answerCollapseState'
import {
    getThreadSessionSnapshot,
    recordThreadSession,
    subscribeToThreadSession,
} from 'lib/threadSession'
import { shouldKeepThreadPostListActive } from 'lib/panelContentActivity'
import { shouldUseThreadReplyCard } from 'lib/threadReplyCards'

const ANSWER_RELATIVE_STYLE = { position: 'relative' }
const EMPTY_DRAG_HANDLE_LISTENERS = {}
const NOOP_DRAG_HANDLE_REF_SETTER = () => {}
const VIRTUAL_TAIL_ANSWER_OVERSCAN = 8
const ACTIVE_TAIL_ANSWER_CHROME_ESTIMATE = 92
const VIRTUAL_DEEP_LINK_MOUNT_TIMEOUT_MS = 2500
const VIRTUAL_DEEP_LINK_SETTLE_TIMEOUT_MS = 3500
const VIRTUAL_DEEP_LINK_STABLE_MS = 220
const NATURAL_DEEP_LINK_SETTLE_TIMEOUT_MS = 5000
const ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX = 2

const nextFrame = () =>
    new Promise(resolve => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(resolve)
            return
        }

        setTimeout(resolve, 0)
    })

const waitForPromiseUntilDeadline = (promise, deadlineAt) =>
    new Promise(resolve => {
        const remainingMs = Math.max(0, deadlineAt - Date.now())
        if (remainingMs === 0) {
            resolve(false)
            return
        }

        let settled = false
        const finish = completed => {
            if (settled) return
            settled = true
            window.clearTimeout(timeoutId)
            resolve(completed)
        }
        const timeoutId = window.setTimeout(() => finish(false), remainingMs)
        Promise.resolve(promise).then(
            () => finish(true),
            () => finish(true),
        )
    })

const findAnswerElement = findAnswerTargetElement

const waitForAnswerElement = ({ scrollElement, answerId, timeoutMs, shouldCancel }) =>
    new Promise(resolve => {
        const startedAt = Date.now()
        const tick = () => {
            if (shouldCancel?.()) {
                resolve(null)
                return
            }

            const element = findAnswerElement(scrollElement, answerId)
            if (element) {
                resolve(element)
                return
            }

            if (Date.now() - startedAt >= timeoutMs) {
                resolve(null)
                return
            }

            nextFrame().then(tick)
        }

        tick()
    })

const resolveElementMetrics = (scrollElement, element) => {
    if (!element) return null

    const elementRect = element.getBoundingClientRect?.()
    const scrollRect = scrollElement?.getBoundingClientRect?.()
    if (!elementRect || !scrollRect) return null

    return {
        height: Math.round(elementRect.height || 0),
        top: Math.round((elementRect.top || 0) - (scrollRect.top || 0)),
    }
}

const answerElementMetrics = (scrollElement, answerId) =>
    resolveElementMetrics(scrollElement, findAnswerElement(scrollElement, answerId))

// Anchor-owner metrics accept the row/skeleton element too, so a still-hydrating open
// placement target can be anchored in the same commit that first renders it.
const answerRowMetrics = (scrollElement, answerId) =>
    resolveElementMetrics(scrollElement, findAnswerRowElement(scrollElement, answerId))

const waitForAnswerElementSettle = ({
    scrollElement,
    answerId,
    requireCentered = false,
    targetTolerancePx = undefined,
    shouldCancel,
    stableMs = VIRTUAL_DEEP_LINK_STABLE_MS,
    timeoutMs = VIRTUAL_DEEP_LINK_SETTLE_TIMEOUT_MS,
}) =>
    new Promise(resolve => {
        const startedAt = Date.now()
        let lastMetrics = null
        let stableSince = Date.now()

        const tick = () => {
            if (shouldCancel?.()) {
                resolve(false)
                return
            }

            const metrics = answerElementMetrics(scrollElement, answerId)
            if (!metrics) {
                stableSince = Date.now()
                lastMetrics = null
            } else if (
                lastMetrics &&
                Math.abs(metrics.top - lastMetrics.top) <= 1 &&
                Math.abs(metrics.height - lastMetrics.height) <= 1
            ) {
                const answerElement = findAnswerElement(scrollElement, answerId)
                if (
                    Date.now() - stableSince >= stableMs &&
                    (!requireCentered ||
                        isAnswerElementNearTarget(scrollElement, answerElement, targetTolerancePx))
                ) {
                    resolve(true)
                    return
                }
            } else {
                lastMetrics = metrics
                stableSince = Date.now()
            }

            if (Date.now() - startedAt >= timeoutMs) {
                resolve(false)
                return
            }

            nextFrame().then(tick)
        }

        tick()
    })

const NATURAL_ANCHOR_SCROLL_KEYS = new Set([
    'ArrowDown',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
    ' ',
])

const installNaturalAnswerAnchorOwner = ({ scrollElement, answerId }) => {
    // The row/skeleton fallback and above-list observation ride the open-placement flag
    // so flag-off anchoring stays byte-for-byte the pre-placement behavior.
    const placementAnchoring = userHasFeature('instant_reply_nav')
    const resolveAnchorMetrics = placementAnchoring ? answerRowMetrics : answerElementMetrics
    const initialMetrics = resolveAnchorMetrics(scrollElement, answerId)
    const targetElement = placementAnchoring
        ? findAnswerRowElement(scrollElement, answerId)
        : findAnswerElement(scrollElement, answerId)
    const segmentList = targetElement
        ?.closest?.('[data-natural-flow-segment]')
        ?.parentElement?.closest?.('[data-natural-flow]')
    const contentColumn = placementAnchoring
        ? scrollElement.querySelector?.('.transform-container')
        : null
    const observationRoot = segmentList || contentColumn
    if (!initialMetrics || !targetElement || !observationRoot) return null

    let anchorTop = initialMetrics.top
    let expectedScrollTop = scrollElement.scrollTop
    let frameId = null
    let released = false
    const previousOverflowAnchor = scrollElement.style.overflowAnchor

    const release = () => {
        if (released) return
        released = true
        if (frameId != null) window.cancelAnimationFrame(frameId)
        resizeObserver?.disconnect()
        mutationObserver?.disconnect()
        scrollElement.removeEventListener('wheel', release)
        scrollElement.removeEventListener('touchstart', release)
        scrollElement.removeEventListener('pointerdown', release)
        scrollElement.removeEventListener('scroll', onScroll)
        window.removeEventListener('keydown', onKeyDown)
        scrollElement.style.overflowAnchor = previousOverflowAnchor
        if (scrollElement.dataset.naturalAnswerAnchorOwned === String(answerId)) {
            delete scrollElement.dataset.naturalAnswerAnchorOwned
        }
    }
    const preserveAnchor = () => {
        if (released || frameId != null) return
        frameId = window.requestAnimationFrame(() => {
            frameId = null
            const metrics = resolveAnchorMetrics(scrollElement, answerId)
            if (!metrics) {
                release()
                return
            }

            const delta = metrics.top - anchorTop
            if (Math.abs(delta) > 0.25) {
                expectedScrollTop = scrollElement.scrollTop + delta
                scrollElement.scrollTop = expectedScrollTop
                expectedScrollTop = scrollElement.scrollTop
            }
            const correctedMetrics = resolveAnchorMetrics(scrollElement, answerId)
            if (correctedMetrics) anchorTop = correctedMetrics.top
        })
    }
    const observeMountedSegments = () => {
        if (!resizeObserver || !segmentList) return
        segmentList
            .querySelectorAll?.('[data-natural-flow-segment-mounted="true"]')
            .forEach(segment => resizeObserver.observe(segment))
    }
    const onKeyDown = event => {
        if (NATURAL_ANCHOR_SCROLL_KEYS.has(event.key)) release()
    }
    const onScroll = () => {
        if (Math.abs(scrollElement.scrollTop - expectedScrollTop) > 0.5) release()
    }
    const resizeObserver =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(preserveAnchor)
    const mutationObserver =
        typeof MutationObserver === 'undefined'
            ? null
            : new MutationObserver(() => {
                  observeMountedSegments()
                  preserveAnchor()
              })

    scrollElement.style.overflowAnchor = 'none'
    scrollElement.dataset.naturalAnswerAnchorOwned = String(answerId)
    resizeObserver?.observe(observationRoot)
    // Title-post media and quest chrome grow ABOVE the segment list, moving it wholesale
    // without resizing it. Observing the whole content column closes that blind spot;
    // preserveAnchor applies only the observed target-top delta, so growth below the
    // target measures as zero and writes nothing. The content column is also the title
    // row's observation root because the title sits before, rather than inside, segments.
    if (contentColumn && contentColumn !== observationRoot) {
        resizeObserver?.observe(contentColumn)
    }
    observeMountedSegments()
    mutationObserver?.observe(observationRoot, {
        attributeFilter: ['data-natural-flow-geometry-ready', 'data-natural-flow-segment-mounted'],
        attributes: true,
        childList: true,
        subtree: true,
    })
    scrollElement.addEventListener('wheel', release, { passive: true })
    scrollElement.addEventListener('touchstart', release, { passive: true })
    scrollElement.addEventListener('pointerdown', release, { passive: true })
    scrollElement.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('keydown', onKeyDown)
    return release
}

const compactNaturalThreadAnswers = answers =>
    (answers || []).map((answer, index) => {
        if (!answer || index === 0) return answer
        return {
            id: answer.id,
            quest_id: answer.quest_id,
            position: answer.position,
            archive: answer.archive,
            action_tags: answer.action_tags,
            vote_total: answer.vote_total,
            crypto_vote_total: answer.crypto_vote_total,
            bsv_upvalue_amt: answer.bsv_upvalue_amt,
            child_quests: answer.child_quests,
            created_at: answer.created_at,
            updated_at: answer.updated_at,
            user_id: answer.user_id || answer.user?.id,
            reply_to_answer_id: answer.reply_to_answer_id,
            user: answer.user?.id == null ? undefined : { id: answer.user.id },
            isLocal: answer.isLocal,
            isPendingOutbox: answer.isPendingOutbox,
            __knovDndId: answer.__knovDndId,
        }
    })

const estimateTailAnswerRowHeight = ({
    answer,
    isActive,
    isThin,
    textIsCollapsed = null,
    useRememberedHeight = true,
    width = 0,
    tweetExpanded = false,
    editing = false,
    replyCard = false,
    chatRun = null,
}) => {
    const estimationSource = resolveHeightEstimationSource(answer)
    const resolvedTextIsCollapsed =
        textIsCollapsed == null ? shouldCollapseAnswerText(estimationSource) : textIsCollapsed

    const reservedHeight = resolveEstimatedAnswerRowHeight({
        answerId: answer?.id,
        answer: estimationSource,
        estimatedWidth: width,
        isActive,
        isThin,
        isTitle: false,
        postLens: replyCard,
        chatRun,
        editMode: editing,
        // The inline composer renders the full uncollapsed text plus editor chrome.
        textIsCollapsed: editing ? false : resolvedTextIsCollapsed,
        activeRowChromeHeight: ACTIVE_TAIL_ANSWER_CHROME_ESTIMATE,
        useRememberedHeight,
        tweetExpanded,
    })
    return editing ? reservedHeight + ANSWER_EDITING_RESERVE_EXTRA_PX : reservedHeight
}

// In the non-dnd path (public quests, inactive quests, users without permission),
// we still render answers through AnswerInView, which injects the *cached* `answer`
// plus collapse-related props. We must consume those injected props rather than
// closing over the stale `answer` from the local list, otherwise optimistic cache
// updates (e.g. synthetic upvalues / locks) won't re-render.
function NonDndAnswerRow({
    answer,
    ix,
    uniqAnswersLength,
    isActive,
    isPublic,
    postLens,
    replyCard,
    dmChatRun,
    dmChatDay,
    quest,
    prevAnswerId,
    nextAnswer,
    isQuoteGuest,
    quoteCount,
    headQuoteCount,
    containerProps,
    titleAnswerProps,
    openRight,
    // injected by AnswerInView
    inViewCallback,
    textIsCollapsed,
    setTextIsCollapsed,
    COLLAPSE_POST_LENGTH,
    estimatedWidth,
    staticGeometry,
}) {
    const isTitle = ix == 0
    if (!answer) return null

    const isThin = !isActive && ix >= 1
    const onClickTailAnswer = ev => {
        openRight({ questId: answer.quest_id, answerId: answer.id }, { metricInputEvent: ev })
    }

    return (
        <div>
            {isThin ? (
                <div
                    className={cn(
                        dndWrapperStyles.tailAnswerContainer,
                        ix === uniqAnswersLength - 1 && dndWrapperStyles.isLast,
                        ix === 1 && dndWrapperStyles.isFirstContainer,
                    )}
                >
                    <ThinAnswer
                        answer={answer}
                        quest={quest}
                        onClick={onClickTailAnswer}
                        contextStyles={cn(
                            dndWrapperStyles.thinAnswer,
                            ix === 1 && dndWrapperStyles.isFirst,
                        )}
                        isLast={ix === uniqAnswersLength - 1}
                        isFirst={ix === 1}
                        streamCardSignals
                    />
                </div>
            ) : (
                <div className={dndWrapperStyles.tailAnswerContainer}>
                    <Answer
                        {...{
                            ...containerProps,
                            ...(isTitle ? titleAnswerProps : {}),
                            answer,
                            quest,
                            isTitle,
                            prevAnswerId,
                            nextAnswer,
                            isQuoteGuest,
                            quoteCount,
                            headQuoteCount,
                            imageSize: isActive ? 'large' : 'medium',
                            tailAnswers: isTitle ? containerProps?.tailAnswers : undefined,
                            isTail: ix >= 1,
                            isThin: false,
                            inStream: !isActive,
                            postLens: isTitle && postLens,
                            replyCard: !isTitle && replyCard,
                            dmChatRun,
                            dmChatDay,
                            isPublic,
                            showControls: true,
                            selectable: true,
                            showDragHandle: false,
                            isFirst: ix === 1,
                            isLast: ix === uniqAnswersLength - 1,
                            dragHandleListeners: EMPTY_DRAG_HANDLE_LISTENERS,
                            dragHandleRefSetter: NOOP_DRAG_HANDLE_REF_SETTER,
                            // injected by AnswerInView
                            inViewCallback,
                            textIsCollapsed,
                            setTextIsCollapsed,
                            COLLAPSE_POST_LENGTH,
                            estimatedWidth,
                            staticGeometry,
                            ref: null,
                            previewRef: null,
                            dragConnector: null,
                        }}
                    />
                </div>
            )}
        </div>
    )
}

export default props => {
    const quest = props.quest
    const staticGeometryEnabled = mediaBucketsEnabled()
    const naturalThreadLayout = !!props.naturalThreadLayout && !!props.isActive
    const naturalFlowLayout = !!props.naturalFeedLayout || naturalThreadLayout
    const containerId = `quest-${quest.id}`
    const threadKey = String(quest?.id || '')
    const parentAnswerId = quest?.parent?.id
    const titleSlotId = `title-slot:${containerId}`

    const { registerContainer, activeId } = useAnswersDndRegistry()
    const {
        panelContentActive = true,
        revealPendingOpenPlacement,
        failPendingOpenPlacement,
    } = usePanelContext()

    const isPublic = quest.public || quest?.team?.public
    const isActive = props.isActive
    const staticAnswerGeometryEligible = staticGeometryEnabled && !naturalFlowLayout
    const canEditQuest = perms.amQuestOwner(quest) || perms.amQuestAdmin(quest)
    const userHasDragAndDropEnabled = useUserHasFeature('drag_and_drop')
    const instantReplyNavEnabled = useUserHasFeature('instant_reply_nav')
    const canDnDInThisQuest = !!(
        isActive &&
        canEditQuest &&
        userHasDragAndDropEnabled &&
        // A quote_main union is ordered by created_at and contains answers
        // owned by another quest. Treat the projection as read-only so a
        // guest cannot be reordered, title-swapped, or moved as branch data.
        !props.quoteAnswers?.length &&
        // Public quests should not allow drag & drop reordering/moves.
        !perms.isPublic(quest)
    )
    const { openRight } = useOpenRight(props.panelId)
    // stream_reply_cards: a known-empty branch card renders its head post through Answer's
    // postLens mode (the rollout switch flows into the title row's render + geometry surfaces).
    // Stream-only — never on the active thread — see docs/stream-cards-architecture.md.
    // A direct-message conversation's card is its latest message, in the same anatomy.
    const postLensCard =
        !isActive && (isPostLensStreamQuest(quest) || !!resolveDirectMessageLatestAnswer(quest))
    const threadReplyCardRows = shouldUseThreadReplyCard({
        isActive,
        isTitle: false,
    })
    const displayAnswers = () => {
        if (quest.is_system && !quest?.parent?.leaderboard_announcement) {
            // TODO: our current notification system shows the first answer of custom system quests :(
            return resolveQuestStreamRenderedAnswers(quest)
        }
        if (isActive) return props.displayAnswers()

        return resolveQuestStreamRenderedAnswers(quest)
    }
    const [answers, setAnswers] = useState(() => {
        const initialAnswers = displayAnswers()
        if (!naturalThreadLayout) return initialAnswers

        recordThreadSession(threadKey, initialAnswers)
        return compactNaturalThreadAnswers(initialAnswers)
    })
    const [expandedTextAnswerIds, setExpandedTextAnswerIds] = useState(
        () => new Set((props.expandedAnswerIds || []).map(String)),
    )
    const [expandedTweetAnswerIds, setExpandedTweetAnswerIds] = useState(() => new Set())
    const [editingAnswerIds, setEditingAnswerIds] = useState(() => new Set())
    const [requestedNaturalAnswerId, setRequestedNaturalAnswerId] = useState(null)
    const [requestedContextAnswerIds, setRequestedContextAnswerIds] = useState([])
    const naturalAnswerNavigationRef = useRef(null)
    const naturalAnswerAnchorCleanupRef = useRef(null)
    const answersRef = useRef(answers)
    const answersContainerRef = useRef(null)
    const [answersContainerWidth, setAnswersContainerWidth] = useState(0)
    const titleRowRef = useRef(null)
    const titleRowMinHeightRef = useRef(null)
    const titleSlotOverlayRef = useRef(null)
    const lastTitleOverlayDebugRef = useRef({ sig: null, at: 0 })

    const updateAnswersContainerWidth = useCallback(node => {
        const nextWidth = Math.ceil(
            Math.max(node?.clientWidth || 0, node?.getBoundingClientRect?.()?.width || 0),
        )
        if (!nextWidth) return

        setAnswersContainerWidth(current =>
            shouldRemeasureRowWidth(current, nextWidth) ? nextWidth : current,
        )
    }, [])

    const dndDebugEnabled = () => {
        try {
            return typeof window !== 'undefined' && !!window.__KNOV_DND_DEBUG_TITLE_SWAP
        } catch {
            return false
        }
    }

    const dndDebugLog = (sig, payload) => {
        if (!dndDebugEnabled()) return
        try {
            const now = Date.now()
            if (
                lastTitleOverlayDebugRef.current.sig !== sig ||
                now - lastTitleOverlayDebugRef.current.at > 250
            ) {
                lastTitleOverlayDebugRef.current.sig = sig
                lastTitleOverlayDebugRef.current.at = now
                // eslint-disable-next-line no-console
                console.log('[dnd:title-slot]', payload)
            }
        } catch {}
    }

    useEffect(() => {
        answersRef.current = answers
    }, [answers])

    const expandedAnswerIdsSignature = (props.expandedAnswerIds || []).map(String).sort().join(',')
    useEffect(() => {
        if (!naturalFlowLayout) return

        const next = new Set(expandedAnswerIdsSignature.split(',').filter(Boolean))
        setExpandedTextAnswerIds(current => (sameSetMembers(current, next) ? current : next))
    }, [expandedAnswerIdsSignature, naturalFlowLayout])

    const isAnswerTextExpanded = useCallback(
        answer => !!answer?.id && expandedTextAnswerIds.has(String(answer.id)),
        [expandedTextAnswerIds],
    )

    const handleTextCollapsedChange = useCallback(
        (answer, textIsCollapsed) => {
            if (!answer?.id) return

            const answerId = String(answer.id)
            setExpandedTextAnswerIds(current => {
                const next = new Set(current)
                if (textIsCollapsed) next.delete(answerId)
                else next.add(answerId)
                if (next.size === current.size && next.has(answerId) === current.has(answerId)) {
                    return current
                }
                return next
            })

            props.onAnswerTextCollapsedChange?.(answer.id, textIsCollapsed)
        },
        [props.onAnswerTextCollapsedChange],
    )

    const isTweetExpandedForAnswer = useCallback(
        answer => !!answer?.id && expandedTweetAnswerIds.has(String(answer.id)),
        [expandedTweetAnswerIds],
    )

    // Tweet-card "more" toggles report through the row geometry events context so the frozen
    // static reserve follows the expanded card instead of clipping it.
    const handleTweetExpandedChange = useCallback(({ answerId, expanded }) => {
        if (!answerId) return

        const normalizedAnswerId = String(answerId)
        setExpandedTweetAnswerIds(current => {
            if (current.has(normalizedAnswerId) === !!expanded) return current
            const next = new Set(current)
            if (expanded) next.add(normalizedAnswerId)
            else next.delete(normalizedAnswerId)
            return next
        })
    }, [])

    const isAnswerEditing = useCallback(
        answer => !!answer?.id && editingAnswerIds.has(String(answer.id)),
        [editingAnswerIds],
    )

    // Inline editing swaps in a composer far taller than the read-mode reserve: track it so
    // the row lifts its clip (and v2 resizes) while the editor is open.
    const handleAnswerEditingChange = useCallback(({ answerId, editing }) => {
        if (!answerId) return

        const normalizedAnswerId = String(answerId)
        setEditingAnswerIds(current => {
            if (current.has(normalizedAnswerId) === !!editing) return current
            const next = new Set(current)
            if (editing) next.add(normalizedAnswerId)
            else next.delete(normalizedAnswerId)
            return next
        })
    }, [])

    // Layout effect (not passive): a cache write re-renders rows with fresh content while the
    // local snapshot still drives keys and reserved sizes. Syncing before paint removes the
    // one-frame window where new content painted inside stale-sized rows.
    useLayoutEffect(
        function updateAnswersOnQuestChange() {
            const nextAnswers = displayAnswers()
            if (naturalThreadLayout) recordThreadSession(threadKey, nextAnswers)
            const next = naturalThreadLayout
                ? compactNaturalThreadAnswers(nextAnswers)
                : nextAnswers
            setAnswers(current => (sameArrayItems(current, next) ? current : next))
        },
        [
            naturalThreadLayout,
            props.quest,
            props.answerFilter,
            props.showAll,
            props.answersShown,
            // quote_main: the union's guest half lands after mount.
            props.quoteAnswers,
            threadKey,
        ],
    )

    const resetAnswers = () => {
        const nextAnswers = displayAnswers()
        if (naturalThreadLayout) recordThreadSession(threadKey, nextAnswers)
        setAnswers(naturalThreadLayout ? compactNaturalThreadAnswers(nextAnswers) : nextAnswers)
    }

    const deleteLocalItem = deleteIndex => {
        setAnswers(answers => {
            let newAnswers = [...answers]
            newAnswers.splice(deleteIndex, 1)[0]
            return newAnswers
        })
    }

    const insertLocalItem = (insertIndex, item) => {
        setAnswers(curr => {
            const next = [...curr]
            next.splice(insertIndex, 0, item)
            return next
        })
    }

    const replaceItemsLocally = (deleteIndex, insertIndex) => {
        setAnswers(curr => {
            const next = [...curr]
            const deletedItem = next.splice(deleteIndex, 1)[0]
            next.splice(insertIndex, 0, deletedItem)
            return next
        })
    }

    // Swap two items in the local list by index (used for live title swap previews).
    const swapItemsLocally = (indexA, indexB) => {
        setAnswers(curr => {
            try {
                const a = Number(indexA)
                const b = Number(indexB)
                if (!Number.isFinite(a) || !Number.isFinite(b)) return curr
                if (a === b) return curr
                if (a < 0 || b < 0) return curr
                if (a >= curr.length || b >= curr.length) return curr
                const next = [...curr]
                const tmp = next[a]
                next[a] = next[b]
                next[b] = tmp
                return next
            } catch {
                return curr
            }
        })
    }

    // Reorder the local list to match the given DnD id order.
    // Used for stable, idempotent title swap previews (avoids swap loops).
    const setItemsOrderLocally = dndIds => {
        setAnswers(curr => {
            try {
                if (!Array.isArray(dndIds) || dndIds.length === 0) return curr
                const map = new Map(curr.map(a => [getDndId(a), a]))
                const next = dndIds.map(id => map.get(String(id))).filter(Boolean)
                return next.length === curr.length ? next : curr
            } catch {
                return curr
            }
        })
    }

    // DnD ids must be globally unique across all rendered lists. However, when we visually
    // move an item between quests during hover, the dragged id must remain stable.
    //
    // We accomplish this by:
    // - Defaulting each answer's dndId to `${containerId}:${answer.id}` (unique per quest)
    // - Allowing a temporary override via `answer.__knovDndId` for cross-container hover moves
    const getDndId = useCallback(
        answer => String(answer?.__knovDndId || `${containerId}:${answer?.id}`),
        [containerId],
    )
    const getVirtualTailAnswerKey = useCallback(
        answer => {
            return getDndId(answer)
        },
        [getDndId],
    )

    const uniqAnswers = useMemo(() => uniqBy(answers, a => getDndId(a)), [answers, getDndId])
    // dm_chat: each message's place in its run (by sender, a few minutes apart)
    // and the day divider above it. Row 0 is the conversation's letter, not a
    // message. Compact natural-thread stubs resolve through the answer cache.
    const dmChat = !!isActive && isDmChatQuest(quest)
    const dmChatRowsById = useMemo(() => {
        if (!dmChat) return null
        const rows = new Map()
        let previous = null
        uniqAnswers.slice(1).forEach(entry => {
            if (entry?.id == null) return
            const message = getCachedAnswer(String(entry.id)) || entry
            rows.set(String(entry.id), {
                run: dmChatRunFor(previous, message),
                day: dmChatDayLabel(previous, message),
            })
            previous = message
        })
        return rows
    }, [dmChat, uniqAnswers])
    const dmChatRowFor = answerId =>
        dmChatRowsById && answerId != null ? dmChatRowsById.get(String(answerId)) || null : null
    const dmChatSignature = answerId => {
        const row = dmChatRowFor(answerId)
        return row ? `chat-${row.run}${row.day ? '-day' : ''}` : 'no-chat'
    }
    // quote_main: count all visible reply edges in this room once, then pass
    // each target's count as an explicit row prop. Compact thread stubs keep
    // reply_to_answer_id, so this is complete before per-answer hydration and
    // changes reactively when the quest projection changes.
    const quoteCountsByTargetId = useMemo(() => {
        const counts = new Map()
        ;(props.quest?.sorted_answers || []).forEach(answer => {
            const targetId = answer?.reply_to_answer_id
            if (!targetId || answer?.archive || answer?.deleted) return
            const key = String(targetId)
            counts.set(key, (counts.get(key) || 0) + 1)
        })
        return counts
    }, [props.quest?.sorted_answers])
    // quote_main: the union's guest half, for the TITLE row's pill — quotes of
    // the head live in the parent room, invisible to the head's own child
    // count (Quest fetches them into quoteAnswers for the active branch).
    const headQuoteCount = useMemo(
        () => (props.quoteAnswers || []).filter(a => a?.id && !a.archive && !a.deleted).length,
        [props.quoteAnswers],
    )
    const items = useMemo(() => uniqAnswers.map(a => getDndId(a)), [getDndId, uniqAnswers])
    const tailAnswers = useMemo(() => answers?.slice(1), [answers])
    const virtualTailAnswers = useMemo(() => uniqAnswers.slice(1), [uniqAnswers])
    useLayoutEffect(() => {
        if (!naturalThreadLayout) return
        recordThreadSession(threadKey, uniqAnswers)
    }, [naturalThreadLayout, threadKey, uniqAnswers])
    const subscribeToCurrentThread = useCallback(
        listener => subscribeToThreadSession(naturalThreadLayout ? threadKey : null, listener),
        [naturalThreadLayout, threadKey],
    )
    const readCurrentThread = useCallback(
        () => getThreadSessionSnapshot(naturalThreadLayout ? threadKey : null),
        [naturalThreadLayout, threadKey],
    )
    const threadSession = useSyncExternalStore(
        subscribeToCurrentThread,
        readCurrentThread,
        readCurrentThread,
    )
    const naturalTailDescriptors = naturalThreadLayout ? threadSession.rows.slice(1) : []
    const isEmpty = uniqAnswers.length === 0
    const isDraggingTitleInThisQuest = !!(
        activeId &&
        parentAnswerId &&
        String(activeId) === `${String(containerId)}:${String(parentAnswerId)}`
    )
    const shouldVirtualizeTailAnswers = shouldVirtualizeAnswerTail({
        isActive: isActive && !naturalFlowLayout,
        activeId,
        canDnDInThisQuest,
        tailAnswerCount: virtualTailAnswers.length,
        minCount: VIRTUAL_TAIL_ANSWER_MIN_COUNT,
    })
    // Keep answer geometry derivation out of the virtualizer's scroll path. This immutable model
    // changes only when content, width, or explicit UI geometry state changes.
    const baseVirtualTailAnswerGeometry = useMemo(() => {
        if (!shouldVirtualizeTailAnswers) return []

        return virtualTailAnswers.map((answer, index) => {
            const key = answer ? getVirtualTailAnswerKey(answer) : `tail-answer-${index}`
            if (!answer) {
                return {
                    key,
                    estimatedHeight: 1,
                    staticSignature: null,
                    resizeIntentSignature: null,
                }
            }

            const estimationSource = resolveHeightEstimationSource(answer)
            const textIsExpanded = isAnswerTextExpanded(answer)
            const tweetExpanded = isTweetExpandedForAnswer(answer)
            const editing = isAnswerEditing(answer)
            const signature = answerHeightContentSignature(estimationSource) || 'none'
            const defaultTextIsCollapsed = shouldCollapseAnswerText(estimationSource)
            const resolvedTextIsCollapsed = textIsExpanded ? false : defaultTextIsCollapsed
            const collapseState = defaultTextIsCollapsed ? 'collapsed' : 'expanded'
            const userCollapseState = textIsExpanded ? 'expanded-user' : collapseState
            const tweetState = tweetExpanded ? 'tweet-expanded' : 'tweet-collapsed'
            const replyCardState = threadReplyCardRows ? 'reply-card' : 'legacy-reply'
            const baseLayoutSignature = `${signature}:${userCollapseState}:${tweetState}:${replyCardState}:width:${answersContainerWidth}`
            const layoutSignature = editing ? `${baseLayoutSignature}:editing` : baseLayoutSignature
            const estimatedHeight = estimateTailAnswerRowHeight({
                answer,
                isActive,
                isThin: false,
                textIsCollapsed: resolvedTextIsCollapsed,
                useRememberedHeight: !staticGeometryEnabled,
                width: answersContainerWidth,
                tweetExpanded,
                editing,
                replyCard: threadReplyCardRows,
            })

            return {
                key,
                estimatedHeight,
                staticSignature: layoutSignature,
                resizeIntentSignature: `${textIsExpanded ? 'text-expanded' : 'text-default'}:${
                    tweetExpanded ? 'tweet-expanded' : 'tweet-collapsed'
                }:${editing ? 'editing' : 'read'}`,
                textIsCollapsed: resolvedTextIsCollapsed,
            }
        })
    }, [
        answersContainerWidth,
        getVirtualTailAnswerKey,
        isActive,
        isAnswerEditing,
        isAnswerTextExpanded,
        isTweetExpandedForAnswer,
        shouldVirtualizeTailAnswers,
        staticGeometryEnabled,
        threadReplyCardRows,
        virtualTailAnswers,
    ])
    const estimateNaturalThreadAnswerHeight = useCallback(
        descriptor => {
            const answer = resolveHeightEstimationSource({ id: descriptor.answerId })
            const answerIdentity = { id: descriptor.answerId }
            const textIsExpanded = isAnswerTextExpanded(answerIdentity)
            const tweetExpanded = isTweetExpandedForAnswer(answerIdentity)
            const editing = isAnswerEditing(answerIdentity)
            return estimateTailAnswerRowHeight({
                answer,
                isActive,
                isThin: false,
                textIsCollapsed: textIsExpanded ? false : shouldCollapseAnswerText(answer),
                useRememberedHeight: true,
                width: answersContainerWidth,
                tweetExpanded,
                editing,
                replyCard: threadReplyCardRows,
                chatRun: dmChatRowFor(descriptor.answerId)?.run || null,
            })
        },
        [
            answersContainerWidth,
            dmChatRowsById,
            isActive,
            isAnswerEditing,
            isAnswerTextExpanded,
            isTweetExpandedForAnswer,
            threadReplyCardRows,
        ],
    )
    const naturalThreadRowLayoutSignature = useCallback(
        descriptor => {
            const answerIdentity = { id: descriptor.answerId }
            const textState = isAnswerTextExpanded(answerIdentity)
                ? 'text-expanded'
                : 'text-default'
            const tweetState = isTweetExpandedForAnswer(answerIdentity)
                ? 'tweet-expanded'
                : 'tweet-default'
            const editState = isAnswerEditing(answerIdentity) ? 'editing' : 'read'
            const replyCardState = threadReplyCardRows ? 'reply-card' : 'legacy-reply'
            const chatState = dmChatSignature(descriptor.answerId)
            return `${descriptor.rowKey}:content:${descriptor.geometryRevision}:${textState}:${tweetState}:${editState}:${replyCardState}:${chatState}:width:${answersContainerWidth}`
        },
        [
            dmChatRowsById,
            answersContainerWidth,
            isAnswerEditing,
            isAnswerTextExpanded,
            isTweetExpandedForAnswer,
            threadReplyCardRows,
        ],
    )
    // A deep-linked open lands the viewport mid-thread, so the first commit should mount
    // the target's visual neighborhood (its segment ± one segment), not just its own row.
    const deepLinkNeighborAnswerIds = useMemo(() => {
        if (!instantReplyNavEnabled || !naturalThreadLayout || !props.filter?.answerId) return []

        const targetAnswerId = String(props.filter.answerId)
        const answerIndex = uniqAnswers.findIndex(
            answer => String(answer?.id || '') === targetAnswerId,
        )
        // Title targets (index 0) open at the top and need no tail neighborhood.
        if (answerIndex < 1) return []

        const segmentSize = THREAD_NATURAL_FLOW_SEGMENT_SIZE
        const targetSegment = Math.floor((answerIndex - 1) / segmentSize)
        const startTailIndex = Math.max(0, (targetSegment - 1) * segmentSize)
        const endTailIndex = (targetSegment + 2) * segmentSize - 1
        const ids = []
        for (let tailIndex = startTailIndex; tailIndex <= endTailIndex; tailIndex += 1) {
            const answer = uniqAnswers[tailIndex + 1]
            if (answer?.id != null) ids.push(String(answer.id))
        }
        return ids
    }, [instantReplyNavEnabled, naturalThreadLayout, props.filter?.answerId, uniqAnswers])
    const pinnedNaturalThreadAnswerIds = useMemo(() => {
        const ids = new Set()
        expandedTextAnswerIds.forEach(id => ids.add(String(id)))
        expandedTweetAnswerIds.forEach(id => ids.add(String(id)))
        editingAnswerIds.forEach(id => ids.add(String(id)))
        uniqAnswers.forEach(answer => {
            if (answer?.isLocal || answer?.isPendingOutbox) ids.add(String(answer.id))
        })
        if (props.filter?.answerId) ids.add(String(props.filter.answerId))
        deepLinkNeighborAnswerIds.forEach(id => ids.add(id))
        requestedContextAnswerIds.forEach(id => ids.add(id))
        if (requestedNaturalAnswerId) ids.add(String(requestedNaturalAnswerId))
        return ids
    }, [
        deepLinkNeighborAnswerIds,
        editingAnswerIds,
        expandedTextAnswerIds,
        expandedTweetAnswerIds,
        props.filter?.answerId,
        requestedNaturalAnswerId,
        requestedContextAnswerIds,
        uniqAnswers,
    ])
    useLayoutEffect(() => {
        // Reply-card media frames clamp to the indented column on both the render and the
        // AnswerInView reserve, so stream post-lens cards and flagged active-thread replies
        // both need the measured row width even when no other virtual layout asks for it.
        if (
            !shouldVirtualizeTailAnswers &&
            !naturalThreadLayout &&
            !postLensCard &&
            !threadReplyCardRows
        )
            return

        const node = answersContainerRef.current
        if (!node) return

        updateAnswersContainerWidth(node)
        if (typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => {
            updateAnswersContainerWidth(node)
        })
        observer.observe(node)

        return () => {
            observer.disconnect()
        }
    }, [
        naturalThreadLayout,
        postLensCard,
        shouldVirtualizeTailAnswers,
        threadReplyCardRows,
        updateAnswersContainerWidth,
    ])
    const resolveAnswerScrollElement = useCallback(
        () =>
            props.panel?.getScrollContainer?.() ||
            answersContainerRef.current?.closest?.('.scroll-container') ||
            null,
        [props.panel],
    )
    const releaseNaturalAnswerAnchor = useCallback(() => {
        naturalAnswerAnchorCleanupRef.current?.()
        naturalAnswerAnchorCleanupRef.current = null
    }, [])
    const ownNaturalAnswerAnchor = useCallback(
        (scrollElement, answerId) => {
            releaseNaturalAnswerAnchor()
            naturalAnswerAnchorCleanupRef.current = installNaturalAnswerAnchorOwner({
                scrollElement,
                answerId,
            })
            return naturalAnswerAnchorCleanupRef.current != null
        },
        [releaseNaturalAnswerAnchor],
    )
    const latestAnswerAnchorContextRef = useRef({ threadKey: null, answerId: '' })
    latestAnswerAnchorContextRef.current = {
        threadKey,
        answerId: String(props.filter?.answerId || ''),
    }
    useEffect(() => {
        if (!panelContentActive) {
            naturalAnswerNavigationRef.current = null
            releaseNaturalAnswerAnchor()
        }
        return () => {
            // Panel activation flips panelContentActive in the same commit that installs the
            // open-placement anchor; tearing the anchor down here would leave the freshly
            // placed target uncompensated. Keep an anchor that still owns the current target
            // in the current thread — unmount teardown lives in the dedicated effect below.
            if (userHasFeature('instant_reply_nav')) {
                const latest = latestAnswerAnchorContextRef.current
                const ownedAnswerId =
                    resolveAnswerScrollElement()?.dataset?.naturalAnswerAnchorOwned
                if (
                    ownedAnswerId &&
                    threadKey === latest.threadKey &&
                    ownedAnswerId === latest.answerId
                ) {
                    return
                }
            }
            releaseNaturalAnswerAnchor()
        }
    }, [
        naturalThreadLayout,
        panelContentActive,
        props.filter?.answerId,
        releaseNaturalAnswerAnchor,
        resolveAnswerScrollElement,
        threadKey,
    ])
    useEffect(
        () => () => {
            naturalAnswerNavigationRef.current = null
            releaseNaturalAnswerAnchor()
        },
        [releaseNaturalAnswerAnchor],
    )
    const openPlacementHydrationSeededRef = useRef(false)
    // Pre-paint open placement: an open-with-target intent is consumed by the first commit
    // that renders the target's row, writing the one permitted permalink scroll before the
    // browser paints and installing the target-top anchor owner in the same commit. Runs
    // every commit with an O(1) bail so data-arrival timing (cached, hydrating, fetched
    // late) needs no dependency bookkeeping; consumption makes it single-shot.
    useLayoutEffect(function applyPendingOpenPlacement() {
        const panel = props.panel
        const intent = panel?.pendingOpenPlacement
        if (!intent || intent.contextScroll) return
        if (!naturalThreadLayout || !panelContentActive) return

        const questId = String(props.quest?.id || '')
        const targetAnswerId = String(props.filter?.answerId || '')
        if (!questId || !targetAnswerId) return
        if (String(intent.questId) !== questId || String(intent.answerId) !== targetAnswerId) {
            return
        }
        // Warm the pinned neighborhood through the batched hydration path as soon as the
        // target descriptor exists, so neighbor rows fill in right after first paint.
        if (!openPlacementHydrationSeededRef.current && deepLinkNeighborAnswerIds.length > 0) {
            openPlacementHydrationSeededRef.current = true
            queueAnswerHydration(deepLinkNeighborAnswerIds, { lane: 'visible' })
        }

        const scrollElement = resolveAnswerScrollElement()
        if (!scrollElement) return
        // Hidden container (display:none panel): geometry is unmeasurable — leave the
        // intent for a visible commit; the reveal coordinator remains the fallback.
        if ((scrollElement.clientHeight || 0) <= 0) return
        if ((scrollElement.scrollTop || 0) > OPEN_PLACEMENT_MAX_START_SCROLL_TOP_PX) {
            // The container already moved (user or a competing reveal) — never yank it.
            panel.consumeOpenPlacement(questId, targetAnswerId)
            markPanelNavMetric('open-placement-fallback', {
                reason: 'container-scrolled',
                panelId: panel.panelId,
                questId,
                answerId: targetAnswerId,
            })
            revealPendingOpenPlacement?.(questId, targetAnswerId)
            return
        }

        const rowElement = findAnswerRowElement(scrollElement, targetAnswerId)
        if (!rowElement) return
        if (!panel.consumeOpenPlacement(questId, targetAnswerId)) return

        const result = placeAnswerRowAtTarget(scrollElement, rowElement)
        if (result.placed) {
            panel.markOpenPlacementApplied(questId, targetAnswerId)
            ownNaturalAnswerAnchor(scrollElement, targetAnswerId)
            markPanelNavMetric('open-placement-applied', {
                panelId: panel.panelId,
                questId,
                answerId: targetAnswerId,
                scrollTop: result.appliedScrollTop,
            })
            revealPendingOpenPlacement?.(questId, targetAnswerId)
        } else {
            markPanelNavMetric('open-placement-fallback', {
                reason: 'write-clamped',
                panelId: panel.panelId,
                questId,
                answerId: targetAnswerId,
                targetScrollTop: result.targetScrollTop,
                appliedScrollTop: result.appliedScrollTop,
            })
        }
    })
    const { registerRow: registerStaticTailAnswerRow, rows: virtualTailAnswerGeometry } =
        useStaticGeometryRowMeasurements({
            enabled: staticGeometryEnabled && !naturalFlowLayout && shouldVirtualizeTailAnswers,
            rows: baseVirtualTailAnswerGeometry,
            resolveScrollElement: resolveAnswerScrollElement,
            measureRow: readStaticAnswerRowMeasurement,
        })
    const {
        listElementRef: virtualTailListElementRef,
        measureElement: measureVirtualTailElement,
        totalSize: virtualTailTotalSize,
        virtualizer: virtualTailList,
        virtualListStart: virtualTailListStart,
        virtualItems: virtualTailItems,
    } = usePanelVirtualizer({
        count: shouldVirtualizeTailAnswers ? virtualTailAnswers.length : 0,
        enabled: shouldVirtualizeTailAnswers && !naturalFlowLayout,
        resolveScrollElement: resolveAnswerScrollElement,
        getItemKey: index => {
            return virtualTailAnswerGeometry[index]?.key ?? `tail-answer-${index}`
        },
        estimateSize: index => virtualTailAnswerGeometry[index]?.estimatedHeight ?? 1,
        overscan: VIRTUAL_TAIL_ANSWER_OVERSCAN,
        staticGeometry: staticGeometryEnabled,
        staticRows: staticGeometryEnabled ? virtualTailAnswerGeometry : undefined,
        // Scalars only. measureDeps feed a legacy-mode (flag-off) effect that calls
        // virtualizer.measure() — a full measured-size cache wipe — so the row-geometry
        // array (fresh identity on every answers/expansion change) must never appear here.
        // Static mode reconciles the immutable row model only when its identity changes.
        measureDeps: [
            props.answerFilter,
            props.showAll,
            props.answersShown,
            shouldVirtualizeTailAnswers,
            virtualTailAnswers.length,
            answersContainerWidth,
            staticGeometryEnabled,
        ],
        scrollMarginDeps: [shouldVirtualizeTailAnswers, uniqAnswers.length],
        observeElementRefs: [answersContainerRef, titleRowRef],
        retainMeasuredScrollMovement: false,
        retainVirtualStartOnTotalSizeChange: true,
    })
    const scrollToVirtualAnswer = useCallback(
        async (answerId, options = {}) => {
            const target = resolveAnswerDeepLinkTarget({ answers: uniqAnswers, answerId })
            if (!target) return 'not-in-quest'
            if (target.isTitle || !shouldVirtualizeTailAnswers) return 'fallback'

            const shouldCancel = options.shouldCancel
            const scrollElement = resolveAnswerScrollElement()
            if (!scrollElement || target.tailIndex < 0) return 'not-mounted'

            const hydrationIds = virtualAnswerTargetWindowIds({
                answers: uniqAnswers,
                answerIndex: target.answerIndex,
            })
            if (hydrationIds.length > 0) {
                await api.getAnswers(hydrationIds).catch(() => null)
            }
            if (shouldCancel?.()) return 'timeout'

            virtualTailList.scrollToIndex(target.tailIndex, {
                align: 'center',
                behavior: 'auto',
            })

            const mountedElement = await waitForAnswerElement({
                scrollElement,
                answerId,
                timeoutMs: VIRTUAL_DEEP_LINK_MOUNT_TIMEOUT_MS,
                shouldCancel,
            })
            if (!mountedElement) return 'not-mounted'

            if (shouldCancel?.()) return 'timeout'
            if (!staticGeometryEnabled) virtualTailList.measure()
            await nextFrame()

            await waitForAnswerElementSettle({
                scrollElement,
                answerId,
                requireCentered: false,
                shouldCancel,
            })
            if (shouldCancel?.()) return 'timeout'
            if (!staticGeometryEnabled) virtualTailList.measure()
            await nextFrame()
            const settledElement = findAnswerElement(scrollElement, answerId)
            if (!settledElement) return 'not-mounted'

            const revealed = await scrollAnswerElementIntoView(scrollElement, settledElement, {
                // The virtualizer already performs the necessary jump to mount the target row.
                // Avoid rewinding to a staged offset when that jump already landed near target.
                forceReveal: false,
                durationMs: options.instant ? 0 : undefined,
                leadOffsetPx: options.instant ? 0 : undefined,
                shouldCancel,
                tolerancePx: ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
            })
            const finalElement = findAnswerElement(scrollElement, answerId)
            return revealed ||
                isAnswerElementNearTarget(
                    scrollElement,
                    finalElement,
                    ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                )
                ? 'done'
                : 'timeout'
        },
        [
            resolveAnswerScrollElement,
            shouldVirtualizeTailAnswers,
            staticGeometryEnabled,
            uniqAnswers,
            virtualTailList,
        ],
    )
    const scrollToNaturalAnswer = useCallback(
        async (answerId, options = {}) => {
            const normalizedAnswerId = String(answerId || '')
            if (
                !naturalThreadLayout ||
                !threadSession.rows.some(descriptor => descriptor.answerId === normalizedAnswerId)
            ) {
                return 'not-in-quest'
            }

            const pendingIntent = props.panel?.pendingOpenPlacement
            const contextIntent =
                pendingIntent?.contextScroll &&
                pendingIntent.questId === String(props.quest?.id || '') &&
                pendingIntent.answerId === normalizedAnswerId
                    ? pendingIntent
                    : undefined
            const contextScroll = options.contextScroll || !!contextIntent

            // A pre-paint open placement (or a completed prior navigation) may already own
            // this target. Re-running the release → hydrate → settle → scroll pipeline
            // would drop the owner mid-flight for nothing; verify in place instead.
            if (!contextScroll && instantReplyNavEnabled) {
                const anchoredScrollElement = resolveAnswerScrollElement()
                if (
                    anchoredScrollElement?.dataset?.naturalAnswerAnchorOwned === normalizedAnswerId
                ) {
                    const anchoredElement = findAnswerRowElement(
                        anchoredScrollElement,
                        normalizedAnswerId,
                    )
                    if (
                        anchoredElement &&
                        isAnswerElementNearTarget(
                            anchoredScrollElement,
                            anchoredElement,
                            ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                        )
                    ) {
                        return 'done'
                    }
                }
            }
            // An unconsumed intent here means placement lost the race to this reveal —
            // consume it so a late data commit cannot re-place underneath the user.
            if (
                !contextIntent &&
                props.panel?.consumeOpenPlacement?.(
                    String(props.quest?.id || ''),
                    normalizedAnswerId,
                )
            ) {
                markPanelNavMetric('open-placement-fallback', {
                    reason: 'reveal-superseded',
                    panelId: props.panel?.panelId,
                    questId: String(props.quest?.id || ''),
                    answerId: normalizedAnswerId,
                })
            }

            releaseNaturalAnswerAnchor()
            const navigationToken = Symbol(`thread-answer:${normalizedAnswerId}`)
            naturalAnswerNavigationRef.current = navigationToken
            setRequestedNaturalAnswerId(normalizedAnswerId)
            const deadlineAt =
                options.deadlineAt || Date.now() + NATURAL_DEEP_LINK_SETTLE_TIMEOUT_MS
            const navigationCancelled = () =>
                options.shouldCancel?.() || naturalAnswerNavigationRef.current !== navigationToken
            const shouldCancel = () => navigationCancelled() || Date.now() >= deadlineAt

            try {
                if (contextScroll) {
                    const scrollElement = resolveAnswerScrollElement()
                    if (!scrollElement) return 'not-mounted'
                    const result = await revealThreadOpen({
                        scrollElement,
                        answerId: normalizedAnswerId,
                        hidden: !!contextIntent,
                        deadlineAt,
                        // Once the gate opens, finish the bounded context hint even if its
                        // readiness deadline expires. User input and retargeting still cancel.
                        shouldCancel: navigationCancelled,
                        prepare: async () => {
                            const { quest: preparedQuest, contextIds } = await prepareOpenRight(
                                props.quest.id,
                                normalizedAnswerId,
                            )
                            if (shouldCancel()) return []
                            if (
                                !preparedQuest ||
                                preparedQuest.__knovAnswerHydrationScope === 'stream-preview'
                            ) {
                                throw new Error('Thread is not ready for reveal')
                            }
                            setRequestedContextAnswerIds(contextIds)
                            return contextIds
                        },
                        onReveal: () => {
                            if (contextIntent) {
                                props.panel.consumeOpenPlacement(
                                    String(props.quest.id),
                                    normalizedAnswerId,
                                )
                                flushSync(() =>
                                    revealPendingOpenPlacement?.(
                                        props.quest.id,
                                        normalizedAnswerId,
                                        contextIntent,
                                    ),
                                )
                            }
                        },
                        retainAnchor: release => {
                            naturalAnswerAnchorCleanupRef.current = release
                        },
                    })
                    if (contextIntent && result !== 'done' && !navigationCancelled()) {
                        failPendingOpenPlacement?.(
                            props.quest.id,
                            normalizedAnswerId,
                            contextIntent,
                            result === 'not-in-quest' ? 'unavailable' : 'timeout',
                        )
                    }
                    return result
                }
                // Fast path: the target surface is already hydrated and mounted, so the
                // hydration round trip and the geometry-settle pre-wait would only delay the
                // (instant) scroll. The post-scroll stabilization pass plus the anchor owner
                // absorb any residual geometry churn.
                let scrollElement = instantReplyNavEnabled ? resolveAnswerScrollElement() : null
                const hydratedFastPath = Boolean(
                    scrollElement && findAnswerElement(scrollElement, normalizedAnswerId),
                )
                if (hydratedFastPath) {
                    void api.getAnswers([normalizedAnswerId]).catch(() => null)
                } else {
                    const hydrationCompleted = await waitForPromiseUntilDeadline(
                        api.getAnswers([normalizedAnswerId]).catch(() => null),
                        deadlineAt,
                    )
                    if (!hydrationCompleted || shouldCancel()) return 'timeout'

                    scrollElement = resolveAnswerScrollElement()
                    if (!scrollElement) return 'not-mounted'
                    const mountedElement = await waitForAnswerElement({
                        scrollElement,
                        answerId: normalizedAnswerId,
                        timeoutMs: VIRTUAL_DEEP_LINK_MOUNT_TIMEOUT_MS,
                        shouldCancel,
                    })
                    if (!mountedElement || shouldCancel()) return 'not-mounted'

                    await waitForAnswerElementSettle({
                        scrollElement,
                        answerId: normalizedAnswerId,
                        requireCentered: false,
                        shouldCancel,
                    })
                    if (shouldCancel()) return 'timeout'
                }

                const settledElement = findAnswerElement(scrollElement, normalizedAnswerId)
                if (!settledElement) return 'not-mounted'
                const revealed = await scrollAnswerElementIntoView(scrollElement, settledElement, {
                    forceReveal: false,
                    durationMs: options.instant ? 0 : undefined,
                    leadOffsetPx: options.instant ? 0 : undefined,
                    shouldCancel,
                })
                if (!revealed || shouldCancel()) return 'timeout'

                // Establish the durable target owner immediately after exact top placement. Late
                // retained-segment, media, and embed geometry is compensated by that owner
                // until actual user intent releases it; it is not gated on a quiet-period guess.
                const geometryReadyElement = findAnswerElement(scrollElement, normalizedAnswerId)
                if (!geometryReadyElement || shouldCancel()) return 'not-mounted'
                const stabilized = await scrollAnswerElementIntoView(
                    scrollElement,
                    geometryReadyElement,
                    {
                        durationMs: options.instant ? 0 : undefined,
                        forceReveal: false,
                        leadOffsetPx: 0,
                        shouldCancel,
                        tolerancePx: ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                    },
                )
                if (!stabilized || shouldCancel()) return 'timeout'
                const anchorOwned = ownNaturalAnswerAnchor(scrollElement, normalizedAnswerId)
                if (anchorOwned) {
                    const segmentlessTarget = findAnswerRowElement(
                        scrollElement,
                        normalizedAnswerId,
                    )
                    if (segmentlessTarget?.closest('[data-natural-flow-segment]')) return 'done'

                    const settledAtTarget = await waitForAnswerElementSettle({
                        scrollElement,
                        answerId: normalizedAnswerId,
                        requireCentered: true,
                        targetTolerancePx: ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                        shouldCancel,
                    })
                    if (!settledAtTarget || shouldCancel()) return 'timeout'

                    const settledSegmentlessTarget = findAnswerRowElement(
                        scrollElement,
                        normalizedAnswerId,
                    )
                    return isAnswerElementNearTarget(
                        scrollElement,
                        settledSegmentlessTarget,
                        ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                    )
                        ? 'done'
                        : 'timeout'
                }
                // If a non-segment target cannot install an owner because its content column is
                // unavailable, exact placement still completes the reveal without an anchor.
                // The head row is the everyday case: it lives outside the natural segments,
                // and a quote of it — every main-thread guest in a branch carries one — must
                // still land its highlight. Placement is verified here directly, so this
                // holds with or without instant_reply_nav.
                const segmentlessTarget = findAnswerRowElement(scrollElement, normalizedAnswerId)
                if (
                    segmentlessTarget &&
                    !segmentlessTarget.closest('[data-natural-flow-segment]') &&
                    isAnswerElementNearTarget(
                        scrollElement,
                        segmentlessTarget,
                        ANSWER_DEEP_LINK_TARGET_TOLERANCE_PX,
                    )
                ) {
                    return 'done'
                }
                return 'timeout'
            } finally {
                if (naturalAnswerNavigationRef.current === navigationToken) {
                    naturalAnswerNavigationRef.current = null
                    setRequestedNaturalAnswerId(null)
                    // Keep the last opened neighborhood pinned; the next context open
                    // replaces it. Unpinning at animation end can evict visible context.
                }
                // A failed context open retains its hidden target intent for an explicit
                // retry. Only a successful reveal or user cancellation may expose it.
                if (!contextIntent) {
                    revealPendingOpenPlacement?.(props.quest?.id, normalizedAnswerId)
                }
            }
        },
        [
            instantReplyNavEnabled,
            naturalThreadLayout,
            ownNaturalAnswerAnchor,
            props.panel,
            props.quest?.id,
            releaseNaturalAnswerAnchor,
            revealPendingOpenPlacement,
            failPendingOpenPlacement,
            resolveAnswerScrollElement,
            threadSession.rows,
        ],
    )

    useEffect(() => {
        if (props.panel?.filter?.questId !== props.quest?.id) return
        if (typeof props.panel?.registerAnswerScroller !== 'function') return

        return props.panel.registerAnswerScroller(
            naturalThreadLayout ? scrollToNaturalAnswer : scrollToVirtualAnswer,
            props.quest?.id,
            answerId =>
                naturalThreadLayout
                    ? threadSession.rows.some(
                          descriptor => descriptor.answerId === String(answerId || ''),
                      )
                    : Boolean(resolveAnswerDeepLinkTarget({ answers: uniqAnswers, answerId })),
        )
    }, [
        naturalThreadLayout,
        props.panel,
        props.quest?.id,
        threadSession.rows,
        uniqAnswers,
        scrollToNaturalAnswer,
        scrollToVirtualAnswer,
    ])

    const { setNodeRef: setDroppableRef } = useDroppable({
        id: containerId,
        disabled: !canDnDInThisQuest,
    })
    const setAnswersContainerRef = useCallback(
        node => {
            answersContainerRef.current = node
            if (shouldVirtualizeTailAnswers || naturalThreadLayout)
                updateAnswersContainerWidth(node)
            setDroppableRef(node)
        },
        [
            naturalThreadLayout,
            setDroppableRef,
            shouldVirtualizeTailAnswers,
            updateAnswersContainerWidth,
        ],
    )

    // Separate drop target for the quest title (index 0).
    // This lets us treat "drop on title" as a swap without live-reordering placeholders.
    const { setNodeRef: setTitleSlotRef } = useDroppable({
        id: titleSlotId,
        disabled: !canDnDInThisQuest,
    })

    // Stabilize the title-slot hitbox while dragging:
    // During swap-preview we reorder the list, which can temporarily move a non-title answer into index 0.
    // If that answer renders shorter (e.g. ThreadInfo differences), the droppable rect can shrink and
    // cause `over` to bounce. We lock a minimum height based on the real title row.
    useEffect(() => {
        try {
            if (!canDnDInThisQuest) return
            // Refresh cached height when not dragging (or if we haven't captured it yet).
            if (!activeId || !titleRowMinHeightRef.current) {
                const h = titleRowRef.current?.getBoundingClientRect?.()?.height
                if (h && h > 0) titleRowMinHeightRef.current = h
            }

            const overlayRect = titleSlotOverlayRef.current?.getBoundingClientRect?.() || null
            dndDebugLog(
                [
                    containerId,
                    parentAnswerId,
                    activeId ? 'drag' : 'idle',
                    Math.round(titleRowMinHeightRef.current || 0),
                ].join('|'),
                {
                    containerId,
                    parentAnswerId,
                    activeId,
                    titleRowMinHeight: titleRowMinHeightRef.current,
                    overlayRect: overlayRect
                        ? {
                              top: overlayRect.top,
                              bottom: overlayRect.bottom,
                              left: overlayRect.left,
                              right: overlayRect.right,
                              height: overlayRect.height,
                          }
                        : null,
                },
            )
        } catch {}
    }, [activeId, canDnDInThisQuest])

    useEffect(() => {
        if (!canDnDInThisQuest) return
        return registerContainer(containerId, {
            // NOTE: this can change if the user drags an answer into/out of index 0
            // (i.e. changing the quest title). Keep it up-to-date for drag permission rules.
            parentAnswerId,
            getTitleSlotRect: () => {
                try {
                    // Use the fixed overlay rect so it doesn't move during swap previews.
                    return titleSlotOverlayRef.current?.getBoundingClientRect?.() || null
                } catch {
                    return null
                }
            },
            // Only active quests with drag enabled should participate in dnd-kit
            canDrop: canDnDInThisQuest,
            getItemIds: () => uniqBy(answersRef.current, a => getDndId(a)).map(a => getDndId(a)),
            deleteLocalItem,
            insertLocalItem,
            replaceItemsLocally,
            swapItemsLocally,
            setItemsOrderLocally,
            resetAnswers,
            resetOtherQuest: props.resetOtherQuest,
            // Commit reorder by id so we don't rely on stale indices
            commitReorder: (answerId, toIndex) => {
                const current = displayAnswers()
                const fromIndex = (current || []).findIndex(a => String(a?.id) === String(answerId))
                if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return
                props.moveAnswer(fromIndex, toIndex)
            },
            moveAnswerBetweenQuests: props.moveAnswerBetweenQuests,
        })
    }, [
        containerId,
        registerContainer,
        canDnDInThisQuest,
        parentAnswerId,
        deleteLocalItem,
        insertLocalItem,
        replaceItemsLocally,
        swapItemsLocally,
        setItemsOrderLocally,
        resetAnswers,
        props.resetOtherQuest,
        props.moveAnswer,
        props.moveAnswerBetweenQuests,
    ])

    const renderDndAnswerRow = (
        answer,
        ix,
        {
            virtualized = false,
            estimatedHeight: virtualizedEstimatedHeight = undefined,
            textIsCollapsed: virtualizedTextIsCollapsed = undefined,
        } = {},
    ) => {
        const isTitle = ix == 0
        if (!answer) return null
        const dndId = getDndId(answer)
        const isThin = !isActive && ix >= 1
        const postLens = isTitle && postLensCard
        const replyCard = !isTitle && threadReplyCardRows
        const usesReplyCardAnatomy = postLens || replyCard
        const dmChatRow = isTitle ? null : dmChatRowFor(answer?.id)
        const answerViewSurface = getAnswerRenderSurface({
            isActive,
            isThin,
            isTitle,
            postLens: usesReplyCardAnatomy,
            chatRun: dmChatRow?.run || null,
        })
        const Wrapper = isTitle ? 'div' : React.Fragment
        const AnswerVisibilityWrapper = virtualized ? VirtualAnswerInView : AnswerInView
        const textIsExpanded = isAnswerTextExpanded(answer)
        const textIsCollapsedOverride = virtualized
            ? virtualizedTextIsCollapsed ?? (textIsExpanded ? false : null)
            : textIsExpanded
            ? false
            : null
        const estimatedHeight = virtualized ? virtualizedEstimatedHeight ?? 1 : undefined
        const wrapperProps = isTitle
            ? {
                  ref: node => {
                      titleRowRef.current = node
                  },
                  style:
                      activeId && titleRowMinHeightRef.current
                          ? { minHeight: titleRowMinHeightRef.current }
                          : undefined,
              }
            : {}

        return (
            <div
                key={dndId}
                className={`answer-map-container`}
                data-answer-row-id={answer?.id}
                data-answer-row-index={ix}
                data-answer-row-surface={answerViewSurface}
                data-answer-row-quest-id={props.quest?.id}
                {...qaScrollSeedDataAttributes(answer, {
                    surface: answerViewSurface,
                    virtualized,
                })}
            >
                <div className="answer-container">
                    <Wrapper {...wrapperProps}>
                        <AnswerVisibilityWrapper
                            key={`${dndId}:${answerViewSurface}`}
                            answerId={answer.id}
                            visibleDefault={props.questIx === 0 && ix < 10}
                            isActive={isActive}
                            holdMounted={
                                naturalFlowLayout ||
                                !!activeId ||
                                (staticGeometryEnabled && !virtualized)
                            }
                            naturalFlow={naturalFlowLayout && !virtualized}
                            isTitle={isTitle}
                            isThin={isThin}
                            postLens={usesReplyCardAnatomy}
                            chatRun={dmChatRow?.run || null}
                            estimatedHeight={estimatedHeight}
                            estimatedWidth={answersContainerWidth || undefined}
                            staticGeometry={
                                staticAnswerGeometryEligible && (virtualized || !isActive)
                            }
                            textIsCollapsedOverride={textIsCollapsedOverride}
                            onTextCollapsedChange={textIsCollapsed =>
                                handleTextCollapsedChange(answer, textIsCollapsed)
                            }
                        >
                            <AnswerDndWrapper
                                {...{
                                    panelId: props.panelId,
                                    isTitle,
                                    imageSize: isActive ? 'large' : 'medium',
                                    tailAnswers: isTitle ? tailAnswers : undefined,
                                    isTail: ix >= 1,
                                    isThin,
                                    inStream: !isActive,
                                    postLens,
                                    replyCard,
                                    dmChatRun: dmChatRow?.run || null,
                                    dmChatDay: dmChatRow?.day || null,
                                    isPublic,
                                    titleAnswerProps: ix == 0 ? props.titleAnswerProps : null,
                                    style: ANSWER_RELATIVE_STYLE,
                                    panel: props.panel,
                                    query: props.filter?.query,
                                    selectable: true,
                                    ix,
                                    isFirst: ix === 1,
                                    // The others row (stream_reply_cards) takes over the card
                                    // bottom, so the last answer row stops being isLast.
                                    isLast: ix === uniqAnswers.length - 1 && !streamOthersRow,
                                    newUserReplying:
                                        ix > 0 && answers[ix - 1]?.user?.id !== answer?.user?.id,
                                    // Previous VISIBLE row in render order (archived rows are
                                    // filtered upstream); the sent quote line suppresses itself
                                    // when its target is this row. ix 0's previous is nothing;
                                    // ix 1's is the title/head answer.
                                    prevAnswerId: ix > 0 ? uniqAnswers[ix - 1]?.id : null,
                                    // The row below answers this one, so the pair is joined by a line
                                    // down the avatar gutter instead of a quote repeating this row.
                                    nextAnswer: uniqAnswers[ix + 1],
                                    // Same line, one row later: the composer below the thread
                                    // is quoting this row, so it is the next link in the chain.
                                    composerQuoteAnswerId: props.composerQuoteAnswerId,
                                    onQuoteInPlace: props.onQuoteInPlace,
                                    composerAlwaysInReach: props.composerAlwaysInReach,
                                    quoteCount:
                                        ix > 0
                                            ? quoteCountsByTargetId.get(String(answer?.id)) || 0
                                            : 0,
                                    headQuoteCount: ix === 0 ? headQuoteCount : 0,
                                    // quote_main union: a NON-TITLE row living in another
                                    // quest is a main-thread quote visiting this branch.
                                    // ix 0 is the head, whose quest_id points at the
                                    // parent room by construction — never a guest.
                                    isQuoteGuest:
                                        ix > 0 &&
                                        !!answer?.quest_id &&
                                        !!props.quest?.id &&
                                        String(answer.quest_id) !== String(props.quest.id),
                                    answerId: answer.id,
                                    answer,
                                    quest: props.quest,
                                    removeAnswer: props.removeAnswer,
                                    isActive,
                                    showInsert: props.showInsert,
                                    insertAnswer: props.insertAnswer,
                                    updateAnswer: props.updateAnswer,
                                    showControls: true,
                                    showBranching: props.showBranching,
                                    showEmbedButton: props.showEmbedButton,
                                    isMobile,
                                    showAll: props.showAll,
                                    sideQuest: props.sideQuest,
                                    cryptoVoteMode:
                                        answer?.crypto_vote_total && answer.crypto_vote_total > 0,
                                    blog: props.blog,
                                    forbidDrop: props.forbidDrop,
                                    showVotes: props.showVotes,
                                    isBsv: props.isBsv,
                                    answerFilter: props.answerFilter,
                                    setAnswerFilter: props.setAnswerFilter,
                                    answerFilterOptions: props.answerFilterOptions,
                                    dndId,
                                    containerId,
                                    activeId,
                                }}
                            />
                        </AnswerVisibilityWrapper>
                    </Wrapper>
                </div>

                {ix == 0 && isActive && <>{props.titleAnswerProps?.expandAnswers}</>}
            </div>
        )
    }

    const renderNonDndAnswerRow = (
        answer,
        ix,
        {
            virtualized = false,
            estimatedHeight: virtualizedEstimatedHeight = undefined,
            textIsCollapsed: virtualizedTextIsCollapsed = undefined,
        } = {},
    ) => {
        const isTitle = ix == 0
        if (!answer) return null
        const isThin = !isActive && ix >= 1
        const postLens = isTitle && postLensCard
        const replyCard = !isTitle && threadReplyCardRows
        const usesReplyCardAnatomy = postLens || replyCard
        const dmChatRow = isTitle ? null : dmChatRowFor(answer?.id)
        const answerViewSurface = getAnswerRenderSurface({
            isActive,
            isThin,
            isTitle,
            postLens: usesReplyCardAnatomy,
            chatRun: dmChatRow?.run || null,
        })
        const AnswerVisibilityWrapper = virtualized ? VirtualAnswerInView : AnswerInView
        const textIsExpanded = isAnswerTextExpanded(answer)
        const textIsCollapsedOverride = virtualized
            ? virtualizedTextIsCollapsed ?? (textIsExpanded ? false : null)
            : textIsExpanded
            ? false
            : null
        const estimatedHeight = virtualized ? virtualizedEstimatedHeight ?? 1 : undefined

        return (
            <div
                key={`${answer?.id}_${props.quest.id}`}
                className={`answer-map-container`}
                data-answer-row-id={answer?.id}
                data-answer-row-index={ix}
                data-answer-row-surface={answerViewSurface}
                data-answer-row-quest-id={props.quest?.id}
                {...qaScrollSeedDataAttributes(answer, {
                    surface: answerViewSurface,
                    virtualized,
                })}
            >
                <div className="answer-container">
                    <AnswerVisibilityWrapper
                        key={`${answer.id}:${answerViewSurface}`}
                        answerId={answer.id}
                        visibleDefault={props.questIx === 0 && ix < 10}
                        isActive={isActive}
                        holdMounted={
                            naturalFlowLayout ||
                            !!activeId ||
                            (staticGeometryEnabled && !virtualized)
                        }
                        naturalFlow={naturalFlowLayout && !virtualized}
                        isTitle={isTitle}
                        isThin={isThin}
                        postLens={usesReplyCardAnatomy}
                        chatRun={dmChatRow?.run || null}
                        estimatedHeight={estimatedHeight}
                        estimatedWidth={answersContainerWidth || undefined}
                        staticGeometry={staticAnswerGeometryEligible && (virtualized || !isActive)}
                        textIsCollapsedOverride={textIsCollapsedOverride}
                        onTextCollapsedChange={textIsCollapsed =>
                            handleTextCollapsedChange(answer, textIsCollapsed)
                        }
                    >
                        {/* @ts-expect-error - AnswerInView injects remaining props into NonDndAnswerRow */}
                        <NonDndAnswerRow
                            {...{
                                ix,
                                // +1 when the others row (stream_reply_cards) renders below, so
                                // internal isLast math stops treating the last reply as
                                // the card bottom.
                                uniqAnswersLength: uniqAnswers.length + (streamOthersRow ? 1 : 0),
                                // Previous VISIBLE row in render order — see renderDndAnswerRow.
                                prevAnswerId: ix > 0 ? uniqAnswers[ix - 1]?.id : null,
                                // The row below answers this one, so the pair is joined by a line
                                // down the avatar gutter instead of a quote repeating this row.
                                nextAnswer: uniqAnswers[ix + 1],
                                quoteCount:
                                    ix > 0 ? quoteCountsByTargetId.get(String(answer?.id)) || 0 : 0,
                                headQuoteCount: ix === 0 ? headQuoteCount : 0,
                                // quote_main union — see renderDndAnswerRow (ix 0 is the
                                // head, never a guest).
                                isQuoteGuest:
                                    ix > 0 &&
                                    !!answer?.quest_id &&
                                    !!props.quest?.id &&
                                    String(answer.quest_id) !== String(props.quest.id),
                                answer,
                                isActive,
                                isPublic,
                                postLens,
                                replyCard,
                                dmChatRun: dmChatRow?.run || null,
                                dmChatDay: dmChatRow?.day || null,
                                quest: props.quest,
                                // Pass the parent props wholesale to preserve existing Answer behavior,
                                // but override specific fields inside NonDndAnswerRow.
                                containerProps: {
                                    ...props,
                                    tailAnswers: isTitle ? tailAnswers : undefined,
                                },
                                titleAnswerProps: isTitle ? props.titleAnswerProps : null,
                                openRight,
                            }}
                        />
                    </AnswerVisibilityWrapper>
                </div>

                {ix == 0 && isActive && <>{props.titleAnswerProps?.expandAnswers}</>}
            </div>
        )
    }

    const renderVirtualizedTailAnswers = renderAnswerRow => (
        // The provider is mounted only on the virtualized (active thread) surface: stream
        // cards keep QuestStream's per-row provider so tweet expansion bumps the QUEST row's
        // geometry there instead of this container's local state.
        <RowGeometryEventsProvider
            questId={quest?.id}
            expandedTweetAnswerIds={expandedTweetAnswerIds}
            onTweetExpandedChange={handleTweetExpandedChange}
            onAnswerEditingChange={handleAnswerEditingChange}
        >
            {renderAnswerRow(uniqAnswers[0], 0)}
            <div
                ref={virtualTailListElementRef}
                style={{
                    height: virtualTailTotalSize,
                    position: 'relative',
                    width: '100%',
                }}
            >
                {virtualTailItems.map(virtualRow => {
                    const answer = virtualTailAnswers[virtualRow.index]
                    if (!answer) return null
                    const rowGeometry = virtualTailAnswerGeometry[virtualRow.index]
                    const ix = virtualRow.index + 1
                    const rowIsEditing = isAnswerEditing(answer)

                    return (
                        <div
                            key={virtualRow.key}
                            ref={node => {
                                measureVirtualTailElement(node)
                                updateStaticRowQaProbe(node, virtualRow.key)
                                registerStaticTailAnswerRow(node, virtualRow.key)
                            }}
                            data-virtual-answer-row="true"
                            data-virtual-row-key={
                                staticGeometryEnabled ? virtualRow.key : undefined
                            }
                            data-virtual-static-geometry={
                                staticGeometryEnabled ? 'true' : undefined
                            }
                            data-virtual-row-size={
                                staticGeometryEnabled ? Math.round(virtualRow.size) : undefined
                            }
                            data-index={virtualRow.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: staticGeometryEnabled ? virtualRow.size : undefined,
                                // The reserve is authoritative: an estimator miss may clip this
                                // row's own content but can never paint over the next row. The
                                // one exception is an open inline composer — lift the clip and
                                // raise the row so its controls stay visible and clickable.
                                overflow:
                                    staticGeometryEnabled && !rowIsEditing ? 'clip' : undefined,
                                zIndex: staticGeometryEnabled && rowIsEditing ? 1 : undefined,
                                transform: `translateY(${
                                    virtualRow.start - virtualTailListStart
                                }px)`,
                            }}
                        >
                            <ScrollResizeCompensationProvider compensate={false}>
                                {renderAnswerRow(answer, ix, {
                                    virtualized: true,
                                    estimatedHeight: rowGeometry?.estimatedHeight,
                                    textIsCollapsed: rowGeometry?.textIsCollapsed,
                                })}
                            </ScrollResizeCompensationProvider>
                        </div>
                    )
                })}
            </div>
        </RowGeometryEventsProvider>
    )

    const keepNaturalThreadPostListActive = shouldKeepThreadPostListActive({
        editingAnswerCount: editingAnswerIds.size,
        hasActiveDrag: !!activeId,
        panelContentActive,
    })
    const renderNaturalThreadAnswers = renderAnswerRow => (
        <ThreadPanelActivityBoundary active={keepNaturalThreadPostListActive}>
            <RowGeometryEventsProvider
                questId={quest?.id}
                expandedTweetAnswerIds={expandedTweetAnswerIds}
                onTweetExpandedChange={handleTweetExpandedChange}
                onAnswerEditingChange={handleAnswerEditingChange}
            >
                <ScrollResizeCompensationProvider compensate={false}>
                    {renderAnswerRow(uniqAnswers[0], 0)}
                    <ThreadNaturalSegments
                        descriptors={naturalTailDescriptors}
                        forceMountAll={!!activeId}
                        getEstimatedAnswerHeight={estimateNaturalThreadAnswerHeight}
                        getRowLayoutSignature={naturalThreadRowLayoutSignature}
                        pinnedAnswerIds={pinnedNaturalThreadAnswerIds}
                        renderAnswer={renderAnswerRow}
                        threadKey={threadKey}
                    />
                </ScrollResizeCompensationProvider>
            </RowGeometryEventsProvider>
        </ThreadPanelActivityBoundary>
    )

    const streamOthersRow = !isActive ? resolveQuestStreamOthersRow(quest, uniqAnswers) : null

    return (
        <div
            ref={setAnswersContainerRef}
            className={cn(
                'no-select-answer-container',
                'limit-selection',
                `answers-container ${props.showStatsClass}`,
            )}
            style={{
                position: 'relative',
                ...(isEmpty && canDnDInThisQuest
                    ? {
                          minHeight: 72,
                          paddingTop: 8,
                          paddingBottom: 8,
                      }
                    : {}),
            }}
            // Ensure there's always a measurable droppable rect even when there are no answers rendered
            // (otherwise `over` can be null and you can't drop into an empty quest).
        >
            {/* Fixed overlay drop target for the title zone.
                This must NOT move with the list items during swap previews. */}
            {canDnDInThisQuest && (
                <div
                    ref={node => {
                        titleSlotOverlayRef.current = node
                        setTitleSlotRef(node)
                    }}
                    data-dnd-title-slot={true}
                    data-dnd-title-slot-id={titleSlotId}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        // IMPORTANT: keep the title "swap zone" small so dragging the first reply back down
                        // exits swap mode quickly (ThreadInfo/expanded title content can be very tall).
                        // This avoids the "must drag much lower than expected" problem.
                        height: Math.min(titleRowMinHeightRef.current || 72, 96),
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />
            )}
            {canDnDInThisQuest ? (
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    {isEmpty && (
                        <div
                            style={{
                                border: '1px dashed rgba(0,0,0,0.25)',
                                borderRadius: 8,
                                padding: '12px 10px',
                                textAlign: 'center',
                                color: 'rgba(0,0,0,0.45)',
                                userSelect: 'none',
                            }}
                        >
                            Drop answer here
                        </div>
                    )}
                    {naturalThreadLayout
                        ? renderNaturalThreadAnswers(renderDndAnswerRow)
                        : shouldVirtualizeTailAnswers
                        ? renderVirtualizedTailAnswers(renderDndAnswerRow)
                        : uniqAnswers.map((answer, ix) => renderDndAnswerRow(answer, ix))}
                </SortableContext>
            ) : (
                <>
                    {naturalThreadLayout
                        ? renderNaturalThreadAnswers(renderNonDndAnswerRow)
                        : shouldVirtualizeTailAnswers
                        ? renderVirtualizedTailAnswers(renderNonDndAnswerRow)
                        : uniqAnswers.map((answer, ix) => renderNonDndAnswerRow(answer, ix))}
                </>
            )}
            {streamOthersRow && (
                <QuestStreamOthersRow
                    quest={quest}
                    othersRow={streamOthersRow}
                    panelId={props.panelId}
                />
            )}
        </div>
    )
}
